import { z } from 'zod';

/*
 * Permissions, Discord style: every member gets the permissions of the team's base role («Todos»)
 * plus those of each of their roles. Channels can then allow or deny channel permissions to the
 * base role, to a role or to one person. Owners, admins and roles with `administrator` have every
 * permission everywhere.
 */

/** Permissions that only make sense for the whole team. */
export const teamPermissions = [
  'administrator',
  'manage_team',
  'manage_roles',
  'manage_channels',
  'create_channels',
  'invite_members',
  'kick_members',
  'manage_webhooks',
  'manage_meetings',
  'manage_tasks',
] as const;

/** Permissions that can also be allowed or denied per channel. */
export const channelPermissions = [
  'view_channel',
  'send_messages',
  'attach_files',
  'add_reactions',
  'pin_messages',
  'manage_messages',
  'connect',
  'speak',
] as const;

export const permissions = [...teamPermissions, ...channelPermissions] as const;
export type TeamPermission = (typeof teamPermissions)[number];
export type ChannelPermission = (typeof channelPermissions)[number];
export type Permission = (typeof permissions)[number];

export const permissionSchema = z.enum(permissions);
export const channelPermissionSchema = z.enum(channelPermissions);

export type PermissionGroup = 'general' | 'members' | 'text' | 'voice';

export const permissionGroups: Record<PermissionGroup, string> = {
  general: 'General',
  members: 'Miembros',
  text: 'Mensajes',
  voice: 'Voz y vídeo',
};

export const permissionInfo: Record<
  Permission,
  { label: string; description: string; group: PermissionGroup }
> = {
  administrator: {
    label: 'Administrador',
    description: 'Tiene todos los permisos en todos los canales. Dalo con cuidado.',
    group: 'general',
  },
  manage_team: {
    label: 'Gestionar el equipo',
    description: 'Cambiar el nombre del equipo.',
    group: 'general',
  },
  manage_roles: {
    label: 'Gestionar roles',
    description: 'Crear, editar y asignar roles, y cambiar los permisos de «Todos».',
    group: 'general',
  },
  manage_channels: {
    label: 'Gestionar canales',
    description: 'Editar y borrar canales y cambiar sus permisos.',
    group: 'general',
  },
  create_channels: {
    label: 'Crear canales',
    description: 'Crear canales de texto y de voz.',
    group: 'general',
  },
  manage_webhooks: {
    label: 'Gestionar integraciones',
    description: 'Crear y borrar webhooks de los canales.',
    group: 'general',
  },
  manage_meetings: {
    label: 'Gestionar reuniones',
    description: 'Editar y cancelar reuniones organizadas por otros.',
    group: 'general',
  },
  manage_tasks: {
    label: 'Gestionar tareas',
    description: 'Borrar tareas creadas por otros.',
    group: 'general',
  },
  invite_members: {
    label: 'Invitar',
    description: 'Enviar invitaciones y crear enlaces para unirse al equipo.',
    group: 'members',
  },
  kick_members: {
    label: 'Expulsar miembros',
    description: 'Sacar del equipo a quien no sea administrador.',
    group: 'members',
  },
  view_channel: {
    label: 'Ver el canal',
    description: 'Ver el canal y leer sus mensajes. Sin él, el canal no aparece.',
    group: 'text',
  },
  send_messages: {
    label: 'Enviar mensajes',
    description: 'Escribir mensajes y responder en hilos.',
    group: 'text',
  },
  attach_files: {
    label: 'Adjuntar archivos',
    description: 'Subir imágenes y archivos.',
    group: 'text',
  },
  add_reactions: {
    label: 'Añadir reacciones',
    description: 'Reaccionar a los mensajes con emojis.',
    group: 'text',
  },
  pin_messages: {
    label: 'Fijar mensajes',
    description: 'Fijar y desfijar mensajes.',
    group: 'text',
  },
  manage_messages: {
    label: 'Gestionar mensajes',
    description: 'Borrar mensajes de otras personas.',
    group: 'text',
  },
  connect: {
    label: 'Unirse a llamadas',
    description: 'Entrar en las llamadas y los canales de voz.',
    group: 'voice',
  },
  speak: {
    label: 'Hablar',
    description: 'Usar el micrófono, la cámara y compartir pantalla en las llamadas.',
    group: 'voice',
  },
};

/** What the base role («Todos») can do in a new team: roughly what members could before roles. */
export const defaultEveryonePermissions: Permission[] = [
  'create_channels',
  'view_channel',
  'send_messages',
  'attach_files',
  'add_reactions',
  'pin_messages',
  'connect',
  'speak',
];

export const setPermissionsSchema = z.object({
  permissions: z.array(permissionSchema).max(permissions.length),
});
export type SetPermissionsInput = z.infer<typeof setPermissionsSchema>;

/* ---------- Channel overrides ---------- */

/** `everyone` is the base role (its target id is the team id), like Discord's @everyone. */
export const overrideTargets = ['everyone', 'role', 'member'] as const;
export type OverrideTarget = (typeof overrideTargets)[number];

export const saveOverrideSchema = z
  .object({
    targetType: z.enum(overrideTargets),
    targetId: z.uuid(),
    allow: z.array(channelPermissionSchema).max(channelPermissions.length),
    deny: z.array(channelPermissionSchema).max(channelPermissions.length),
  })
  .refine((v) => !v.allow.some((p) => v.deny.includes(p)), {
    message: 'Un permiso no puede estar permitido y denegado a la vez',
    path: ['deny'],
  });
export type SaveOverrideInput = z.infer<typeof saveOverrideSchema>;

export interface PermissionOverride {
  targetType: OverrideTarget;
  targetId: string;
  allow: ChannelPermission[];
  deny: ChannelPermission[];
}

/** What a person is in the team, for resolving permissions. */
export interface PermissionSubject {
  userId: string;
  /** Owner and admins have every permission. */
  isAdmin: boolean;
  /** Base role plus their roles' permissions. */
  base: readonly Permission[];
  roleIds: readonly string[];
}

const isChannelPermission = (p: Permission): p is ChannelPermission =>
  (channelPermissions as readonly string[]).includes(p);

/** Permissions of the team (outside any channel). */
export function resolveTeamPermissions(subject: PermissionSubject): Permission[] {
  if (subject.isAdmin || subject.base.includes('administrator')) return [...permissions];
  return [...new Set(subject.base)];
}

/**
 * Permissions of a person in a channel, applied in Discord's order: base permissions, then the
 * base role's override, then all of their roles' overrides together (denies first), then their own.
 */
export function resolveChannelPermissions(
  subject: PermissionSubject,
  overrides: readonly PermissionOverride[],
): Permission[] {
  const team = resolveTeamPermissions(subject);
  if (team.includes('administrator')) return team;
  const result = new Set(team);
  const apply = (deny: Iterable<ChannelPermission>, allow: Iterable<ChannelPermission>) => {
    for (const p of deny) result.delete(p);
    for (const p of allow) result.add(p);
  };
  const everyone = overrides.find((o) => o.targetType === 'everyone');
  if (everyone) apply(everyone.deny, everyone.allow);
  const roles = overrides.filter(
    (o) => o.targetType === 'role' && subject.roleIds.includes(o.targetId),
  );
  apply(
    roles.flatMap((o) => o.deny),
    roles.flatMap((o) => o.allow),
  );
  const own = overrides.find((o) => o.targetType === 'member' && o.targetId === subject.userId);
  if (own) apply(own.deny, own.allow);
  // Without seeing the channel nothing else in it applies.
  if (!result.has('view_channel')) {
    for (const p of [...result]) if (isChannelPermission(p)) result.delete(p);
  }
  return [...result];
}
